/**
 * Chapter merging utilities.
 *
 * Detects front matter (cover, table of contents, preface, etc.) and merges
 * consecutive spine files into logical chapters based on TOC boundaries (all levels).
 * Uses the deepest TOC level per spine item so chapter view shows one chapter per screen.
 */
import { hrefMatches } from './parseEpub.js';

const FRONT_MATTER_LABELS = [
  'cover', 'contents', 'table of contents', 'introduction', 'preface', 'foreword',
  'acknowledgements', 'acknowledgment', 'dedication', 'prologue', 'copyright',
  'title page', 'half title',
  '封面', '目录', '前言', '序言', '引言', '致谢', '版权',
];

const BACK_MATTER_LABELS = [
  'acknowledgments', 'acknowledgement', 'acknowledgements', 'about the author',
  'about the authors', 'author note', 'index', 'notes', 'bibliography', 'credits',
  'connect with', 'connect with hmh', 'also by', 'license',
  '致谢', '关于作者', '索引', '附录',
];

/** Labels that should be excluded from TOC entirely (e.g. Gutenberg license pages). */
const EXCLUDE_TOC_LABELS = [
  /project\s+gutenberg.*license/i,
];

function isFrontMatterLabel(label) {
  const n = (label || '').trim().toLowerCase();
  return FRONT_MATTER_LABELS.some((k) => n === k || n.startsWith(k + ' ') || n.endsWith(' ' + k));
}

function isBackMatterLabel(label) {
  const n = (label || '').trim().toLowerCase();
  return BACK_MATTER_LABELS.some((k) => n === k || n.startsWith(k + ' ') || n.endsWith(' ' + k));
}

// stripHtmlToText removed (2026-07): structural word/char counts moved to
// lib/epub/structuralCounts.js, which counts on the parity extraction
// (extractVisibleText) instead of this naive regex tag-strip — the regex kept
// <script>/<style> text, Gutenberg boilerplate, hidden elements and math
// source, and split words at inline tags (wo<i>r</i>d → 3 tokens), running
// ~1-2% high vs chapter_registry.word_count.
// countWords removed — use countTextUnits from chunks/textUnits.js

/**
 * Merge spine items into effective (logical) chapters.
 * Uses all TOC levels (not just level-0) so that "chapter" view shows one chapter per screen
 * when the TOC has chapter-level entries (e.g. Part 1 > Chapter 1, Chapter 2, ...).
 *
 * @param {Array} spine - Original spine array [{ href }]
 * @param {Array} tocItems - Raw TOC from parseTOC
 * @param {Array} allChaptersHTML - HTML string per spine item
 * @returns {{ effectiveSpine, effectiveHTML, frontMatterSpineEndIndex, bodyStarts, chapterBoundaries, firstBodyBoundaryIdx, lastBodyChapterEffectiveIndex, firstContentChapterIndex, lastContentChapterIndex }}
 */
export function mergeChapters(spine, tocItems, allChaptersHTML) {
  const chapterBoundaries = [];

  // Filter out excluded TOC entries (e.g. Project Gutenberg License)
  const filteredTocItems = tocItems.filter((item) => {
    const label = item.label || item.title || '';
    return !EXCLUDE_TOC_LABELS.some((re) => re.test(label));
  });

  for (const item of filteredTocItems) {
    const si = spine.findIndex((s) => hrefMatches(item.href, s.href));
    if (si >= 0) {
      const label = item.label || item.title || '';
      chapterBoundaries.push({
        spineIdx: si,
        tocItem: item,
        isFrontMatter: isFrontMatterLabel(label),
        isBackMatter: isBackMatterLabel(label),
      });
    }
  }
  chapterBoundaries.sort((a, b) => a.spineIdx - b.spineIdx);

  // Deduplicate by spine index: keep the entry with the deepest (highest) level so we get
  // chapter-level boundaries (e.g. "Chapter 2") instead of part-level ("Part 1") when both exist.
  const deduped = new Map();
  for (const b of chapterBoundaries) {
    const existing = deduped.get(b.spineIdx);
    const bLevel = b.tocItem.level ?? 0;
    if (!existing) {
      deduped.set(b.spineIdx, b);
    } else {
      const existingLevel = existing.tocItem.level ?? 0;
      const existingLabel = (existing.tocItem.label || existing.tocItem.title || '').trim();
      const newLabel = (b.tocItem.label || b.tocItem.title || '').trim();
      const existingIsBareNum = /^\d+$/.test(existingLabel);
      const newIsBareNum = /^\d+$/.test(newLabel);
      const preferNew = bLevel > existingLevel || (existingIsBareNum && !newIsBareNum);
      const preferExisting = bLevel < existingLevel || (!existingIsBareNum && newIsBareNum);
      if (preferNew) {
        deduped.set(b.spineIdx, { ...b, _numericLabel: existingIsBareNum ? existingLabel : (b._numericLabel ?? existing._numericLabel) });
      } else if (preferExisting) {
        deduped.set(b.spineIdx, { ...existing, _numericLabel: newIsBareNum ? newLabel : (existing._numericLabel ?? b._numericLabel) });
      } else {
        deduped.set(b.spineIdx, bLevel >= existingLevel ? b : existing);
      }
    }
  }
  chapterBoundaries.length = 0;
  for (const b of deduped.values()) chapterBoundaries.push(b);
  chapterBoundaries.sort((a, b) => a.spineIdx - b.spineIdx);

  let firstBodyBoundaryIdx = chapterBoundaries.findIndex((b) => !b.isFrontMatter);
  if (firstBodyBoundaryIdx < 0) firstBodyBoundaryIdx = chapterBoundaries.length;

  const firstBodySpineIdx =
    firstBodyBoundaryIdx < chapterBoundaries.length
      ? chapterBoundaries[firstBodyBoundaryIdx].spineIdx
      : spine.length;

  const bodyStarts = chapterBoundaries.slice(firstBodyBoundaryIdx).map((b) => b.spineIdx);

  const effectiveHTML = [];
  const effectiveSpine = [];

  // Front matter: merge spine[0 .. firstBodySpineIdx-1]
  if (firstBodySpineIdx > 0) {
    effectiveHTML.push(allChaptersHTML.slice(0, firstBodySpineIdx).join(''));
    effectiveSpine.push({ href: spine[0].href });
  }

  // Body chapters: each from bodyStarts[i] to bodyStarts[i+1]-1
  for (let i = 0; i < bodyStarts.length; i++) {
    const start = bodyStarts[i];
    const end = bodyStarts[i + 1] ?? spine.length;
    effectiveHTML.push(allChaptersHTML.slice(start, end).join(''));
    effectiveSpine.push({ href: spine[start].href });
  }

  // Fallback: if no body chapters, use merged everything
  if (effectiveHTML.length === 0) {
    effectiveHTML.push(allChaptersHTML.join(''));
    effectiveSpine.push({ href: spine[0]?.href });
  }

  // Last "body" chapter index in effectiveSpine (for "标记已读完": show when in last body chapter or after)
  let lastBodyChapterEffectiveIndex = effectiveSpine.length - 1;
  if (bodyStarts.length > 0) {
    let lastNonBackMatterIdx = firstBodyBoundaryIdx;
    for (let i = firstBodyBoundaryIdx; i < chapterBoundaries.length; i++) {
      if (!chapterBoundaries[i].isBackMatter) lastNonBackMatterIdx = i;
    }
    const frontMatterCount = firstBodySpineIdx > 0 ? 1 : 0;
    lastBodyChapterEffectiveIndex = frontMatterCount + (lastNonBackMatterIdx - firstBodyBoundaryIdx);
    if (lastBodyChapterEffectiveIndex < 0) lastBodyChapterEffectiveIndex = 0;
    if (lastBodyChapterEffectiveIndex >= effectiveSpine.length) lastBodyChapterEffectiveIndex = effectiveSpine.length - 1;
  }

  // Word/char counts + progress weights moved to lib/epub/structuralCounts.js
  // (parity extraction; see note at stripHtmlToText's former site above).
  // Kept here: pure structure — spine slicing, boundaries, content indices.
  const firstContentChapterIndex = firstBodySpineIdx > 0 ? 1 : 0;
  const lastContentChapterIndex = lastBodyChapterEffectiveIndex;

  return {
    effectiveSpine,
    effectiveHTML,
    frontMatterSpineEndIndex: firstBodySpineIdx,
    bodyStarts,
    chapterBoundaries,
    firstBodyBoundaryIdx,
    lastBodyChapterEffectiveIndex,
    firstContentChapterIndex,
    lastContentChapterIndex,
  };
}
